import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineCalendar,
  HiOutlineBuildingOffice,
  HiOutlineUserGroup,
  HiOutlineIdentification,
  HiOutlineGlobeAlt,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineUsers,
  HiOutlineArrowDownTray,
} from "react-icons/hi2";
import Button from "components/button/Button";
import { useGetAssessmentByIdQuery } from "reduxFiles/api/assessmentApi";
import ScoringResultsTable from "./ScoringResultsTable";
import ReportTabs, { ReportType } from "./ReportTabs";
import { AnimatePresence, motion } from "framer-motion";
import { useScope } from "hooks/useScope";
import { HiOutlineDocumentReport } from "react-icons/hi";
import PAEISection from "./PAEISection";
import RadarChartComponent from "./charts/RadarChartComponent";
import IndustryLifeCycle from "./industryLifeCycle/IndustryLifeCycle";
import ChartsTab from "./chartsTab/ChartsTab";
import StrategyReport from "./strategy/StrategyReport";
import { industryWieghts } from "variables/industryWieghts";
import { getDisplayPercent, useIndustryLifeCycle } from "./helper/reportHelper";
import { calculateCompleteStrategy } from "./helper/strategyCalculations";
import { useAssessmentReportSync } from "hooks/useAssessmentReportSync";
import { LifeCyclePosition, StrategyStability } from "types/assessmentTypes";
import UpgradePlanMessage from "./upgrade/UpgradePlanMessage";
import { useAssessmentPdfExport } from "./export/useAssessmentPdfExport";

export default function AssessmentReport() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const scope = useScope();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { exportPdf, isExporting, progress } = useAssessmentPdfExport();
  const [activeReportTab, setActiveReportTab] =
    useState<ReportType>("criteria");

  const {
    data: assessment,
    isFetching,
    isError,
    refetch,
  } = useGetAssessmentByIdQuery(
    { scope: scope, id: Number(id) },
    {
      skip: !id,
    }
  );

  const hasReportData = useMemo(() => {
    if (!assessment?.report) return false;

    const report = assessment.report;
    return (
      report.ohi !== null &&
      report.ohi !== undefined &&
      report.lifeCycle?.score !== null &&
      report.lifeCycle?.score !== undefined &&
      report.lifeCycle?.position !== null &&
      report.lifeCycle?.position !== undefined &&
      report.strategy?.stability !== null &&
      report.strategy?.stability !== undefined &&
      report.strategy?.order !== null &&
      report.strategy?.order !== undefined
    );
  }, [assessment]);

  // ===== 1. OHI =====
  const calculatedOhi = useMemo(() => {
    if (hasReportData) return 0;

    if (!assessment?.scoringResults?.[0]?.criteriaBreakdown) return 0;

    const industryKey = assessment.company?.industrySector?.key;
    const criteriaBreakdown = assessment.scoringResults[0].criteriaBreakdown;

    let totalWeightedScore = 0;
    let allWeightsSum = 0;
    const rawWeights: number[] = [];

    criteriaBreakdown.forEach((criteria) => {
      const weight =
        industryKey &&
        industryWieghts[
          criteria.criteriaIndex as keyof typeof industryWieghts
        ]?.[industryKey];
      if (typeof weight === "number") {
        rawWeights.push(weight);
        allWeightsSum += weight;
      } else {
        rawWeights.push(0);
      }
    });

    criteriaBreakdown.forEach((criteria, index) => {
      const rawWeight = rawWeights[index] || 0;
      const normalizedWeight =
        allWeightsSum > 0 ? rawWeight / allWeightsSum : 0;
      const displayPercent = getDisplayPercent(criteria);
      totalWeightedScore += displayPercent * normalizedWeight;
    });

    return totalWeightedScore;
  }, [assessment, hasReportData]);

  // ===== 2. LifeCtcle =====
  const lifeCycleResult = useIndustryLifeCycle(
    hasReportData ? [] : assessment?.scoringResults || [],
    hasReportData ? "" : assessment?.company?.industrySector?.key || ""
  );

  const calculatedLifeCycle = useMemo(() => {
    if (hasReportData) {
      return { score: 0, position: LifeCyclePosition.INFANCY };
    }

    if (!lifeCycleResult) {
      return { score: 0, position: LifeCyclePosition.INFANCY };
    }

    return {
      score: Math.round(lifeCycleResult.totalScore * 100),
      position: lifeCycleResult.stageKey || LifeCyclePosition.INFANCY,
    };
  }, [lifeCycleResult, hasReportData]);

  // ===== 3. Strategy =====
  const calculatedStrategy = useMemo(() => {
    if (hasReportData) {
      return { stability: StrategyStability.Low, order: 0 };
    }

    if (
      !assessment?.scoringResults?.[0]?.criteriaBreakdown ||
      !assessment?.company?.industrySector?.key
    ) {
      return { stability: StrategyStability.Low, order: 0 };
    }

    try {
      const strategyResult = calculateCompleteStrategy(
        assessment.scoringResults[0].criteriaBreakdown,
        industryWieghts,
        assessment.company.industrySector.key
      );

      return {
        stability:
          strategyResult?.finalStrategy?.StrategicStability ||
          StrategyStability.Low,
        order: strategyResult?.finalStrategy?.order || 0,
      };
    } catch (error) {
      console.error("Error calculating strategy:", error);
      return { stability: StrategyStability.Low, order: 0 };
    }
  }, [assessment, hasReportData]);

  // async
  const { isUpdating } = useAssessmentReportSync({
    assessment,
    isFetching,
    calculatedOhi,
    calculatedLifeCycleScore: calculatedLifeCycle.score,
    calculatedLifeCyclePosition: calculatedLifeCycle.position,
    calculatedStrategyStability: calculatedStrategy.stability,
    calculatedStrategyOrder: calculatedStrategy.order,
    scope,
  });

  // ===== Render Functions =====
  const renderStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      {
        color: string;
        icon: any;
        label: string;
      }
    > = {
      PENDING: {
        color:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
        icon: HiOutlineClock,
        label: t("assessment.detail.status.PENDING"),
      },
      COMPLETED: {
        color:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        icon: HiOutlineCheckCircle,
        label: t("assessment.detail.status.COMPLETED"),
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium ${config.color}`}
      >
        <Icon className="h-3.5 w-3.5" />
        {config.label}
      </span>
    );
  };

  const renderReportContent = () => {
    if (!assessment) return null;

    const isNormalLicense = assessment?.license?.tier === "NORMAL";

    switch (activeReportTab) {
      case "criteria":
        return (
          <div className="space-y-8">
            <PAEISection
              criteriaBreakdown={
                assessment.scoringResults?.[0]?.criteriaBreakdown || []
              }
              company={assessment.company}
            />

            <RadarChartComponent
              criteriaBreakdown={
                assessment.scoringResults?.[0]?.criteriaBreakdown || []
              }
              title={t("assessment.report.radarChart")}
              description={t("assessment.report.radarChartdesc")}
              height={800}
            />

            <ScoringResultsTable
              scoringResults={assessment.scoringResults || []}
            />
          </div>
        );
      case "lifeCycle":
        return (
          <div className="space-y-8">
            <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white dark:border-gray-800 dark:bg-navy-900">
              <IndustryLifeCycle
                scoringResults={assessment.scoringResults || []}
                company={assessment.company}
              />
            </div>
          </div>
        );

      case "performanceIndicators":
        if (isNormalLicense && scope !== "admin") {
          return <UpgradePlanMessage assessmentId={assessment.id} />;
        }
        return <ChartsTab assessment={assessment} />;

      case "strategy":
        if (isNormalLicense && scope !== "admin") {
          return <UpgradePlanMessage assessmentId={assessment.id} />;
        }
        return (
          <div className="space-y-8">
            <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white dark:border-gray-800 dark:bg-navy-900">
              <StrategyReport
                criteriaBreakdown={
                  assessment.scoringResults?.[0]?.criteriaBreakdown || []
                }
                company={assessment.company}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ===== Early Returns =====
  if (isFetching) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="loader w-8 text-brand-500" />
      </div>
    );
  }

  if (isError || !assessment) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <HiOutlineClipboardDocumentList className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("assessment.detail.notFound")}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t("assessment.detail.notFoundDesc")}
          </p>
          <Button
            variant="primary"
            className="mt-4"
            onClick={() => navigate(-1)}
          >
            {t("assessment.detail.back")}
          </Button>
        </div>
      </div>
    );
  }

  const company = assessment.company;

  const infoItems = [
    {
      icon: HiOutlineClipboardDocumentList,
      label: t("company.fields.nationalId"),
      value: company.nationalId || "-",
    },
    {
      icon: HiOutlineIdentification,
      label: t("company.fields.industrySectorId"),
      value: t(`industrySectors.${company.industrySector?.key}`) || "-",
    },
    {
      icon: HiOutlineGlobeAlt,
      label: t("organization.name"),
      value: company.organization
        ? `${company.organization?.name} ${
            company.organization?.province?.name || ""
          }`
        : "-",
    },
    {
      icon: HiOutlineUsers,
      label: t("company.fields.activityType"),
      value: t(`company.activityTypes.${company.activityType}`) || "-",
    },
    {
      icon: HiOutlineUserGroup,
      label: t("company.fields.user"),
      value: company.user?.fullname || "-",
    },
  ];

  const isNormalLicense = assessment?.license?.tier === "NORMAL";
  const canExportPremium = !(isNormalLicense && scope !== "admin");

  const handleDownloadPdf = async () => {
    if (!assessment || isExporting) return;
    try {
      await exportPdf(assessment, { includePremium: canExportPremium });
    } catch (error) {
      console.error("PDF export failed:", error);
    }
  };

  return (
    <div className="dark:to-navy-950 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-navy-900">
      <div className="container mx-auto p-2">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-navy-800"
        >
          <div className="absolute left-0 right-0 top-0 h-1.5 bg-gradient-to-r from-brand-500 via-purple-500 to-brand-300" />

          <div className="border-b border-gray-100 px-6 pb-2 pt-5 dark:border-navy-700 md:px-8">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
              <div className="flex items-center gap-4">
                <div className="rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 p-3 shadow-lg shadow-brand-500/25">
                  <HiOutlineDocumentReport className="h-8 w-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-4">
                    <h1 className="text-xl font-bold text-navy-700 dark:text-white md:text-2xl">
                      {t("assessment.report.analytics")}
                    </h1>
                    {scope === "user" && isUpdating && (
                      <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                        <div className="loader h-3 w-3" />
                        <span>{t("assessment.report.syncing")}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-700 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <HiOutlineCalendar className="h-4 w-4" />
                      {t("assessment.detail.createdAt")} :{" "}
                      {new Date(assessment.createdAt).toLocaleDateString(
                        lang === "fa" ? "fa-IR" : "en-US"
                      )}
                    </span>
                    <span className="hidden h-4 w-px bg-gray-300 dark:bg-gray-600 md:block" />
                    {renderStatusBadge(assessment.status)}
                    <span className="hidden h-4 w-px bg-gray-300 dark:bg-gray-600 md:block" />
                    <span className="flex items-center gap-1">
                      <HiOutlineCalendar className="h-4 w-4" />
                      {t("organizationAssessmentReport.completedAt")} :{" "}
                      {new Date(assessment.completedAt).toLocaleDateString(
                        lang === "fa" ? "fa-IR" : "en-US"
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex w-full flex-col items-stretch gap-1 md:w-auto md:items-end">
                <div className="flex w-full items-center gap-3 md:w-auto">
                  {isExporting && progress ? (
                    <span className="text-xs text-brand-600 dark:text-brand-300">
                      {t("assessment.report.pdf.progress", {
                        current: progress.current,
                        total: progress.total,
                      })}
                    </span>
                  ) : null}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleDownloadPdf}
                    loading={isExporting}
                    disabled={
                      isExporting ||
                      !assessment.scoringResults ||
                      assessment.scoringResults.length === 0
                    }
                    className="flex-1 md:flex-none"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <HiOutlineArrowDownTray className="h-4 w-4" />
                      {t("assessment.report.pdf.button")}
                    </span>
                  </Button>
                  <Button
                    variant="info"
                    size="sm"
                    onClick={refetch}
                    loading={isFetching}
                    disabled={isExporting}
                    className="flex-1 md:flex-none"
                  >
                    {t("buttons.refresh")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(-1)}
                    disabled={isExporting}
                    className="flex-1 md:flex-none"
                  >
                    {t("assessment.detail.back")}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden rounded-xl border border-gray-100 bg-white/80 shadow-sm backdrop-blur-sm dark:border-navy-700 dark:bg-navy-800/80"
          >
            <div className="ms-8 flex items-center gap-2 p-2">
              <div className="rounded-lg bg-brand-50 p-1.5 dark:bg-brand-900/20">
                <HiOutlineBuildingOffice className="h-4 w-4 text-brand-500 dark:text-brand-400" />
              </div>
              <h3 className="text-md font-extrabold text-navy-800 dark:text-white">
                {t("company.fields.name")} : {company.name}
              </h3>
            </div>

            <div className="grid grid-cols-2 divide-x divide-y divide-gray-100/80 dark:divide-navy-700/80 sm:grid-cols-3 lg:grid-cols-5">
              {infoItems.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center justify-center px-3 py-3 text-center"
                >
                  <item.icon className="mb-1 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-[12px] font-medium uppercase tracking-wider text-gray-700 dark:text-gray-500">
                    {item.label}
                  </span>
                  <span className="mt-0.5 text-sm font-semibold text-navy-700 dark:text-white">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {assessment.scoringResults && assessment.scoringResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <div className="rounded-2xl bg-white shadow-lg dark:bg-navy-800">
              <ReportTabs
                activeTab={activeReportTab}
                onTabChange={setActiveReportTab}
                isNormalLicense={isNormalLicense}
              />

              <div className="p-4 md:p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeReportTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {renderReportContent()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
